import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { wrap } from 'utils/wrap';
import DOMPurify from 'isomorphic-dompurify';
import { useWindowSize } from 'hooks';
import { Underline } from 'components';
import SVGMapper from 'images/service-pages/event-and-conferences/dropdown-svgs/SVGMapper';

import Arrow from './Arrow';

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    };
  }
};

const ServiceCarousel = ({ items, title, description }) => {
  const { windowWidth } = useWindowSize();

  const [[page, direction], setPage] = useState([10, 0]);

  const paginate = (dir) => {
    setPage([page + dir, dir]);
  };

  const selectPage = (selectedPage) => {
    setPage([selectedPage, 1]);
  };

  const index = wrap(0, items.length, page);

  useEffect(() => {
    const timer = setTimeout(() => paginate(1), 15000);
    return () => clearTimeout(timer);
  }, [page]);

  return (
    <>
      <Title>
        {title}
        <Underline color="#008BEA" width={3} />
      </Title>
      <Description>
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
      </Description>
      <NumberContainer
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <NumberBox
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              selectPage(i);
            }}
            selected={items[index].id === i + 1}>
            <span>{String(i + 1).padStart(2, '0')}</span>
          </NumberBox>
        ))}
        <Prev onClick={() => paginate(-1)} disabled={page === 0} />
        <Next onClick={() => paginate(1)} disabled={page === 9} id="omnichannel-carousel-click" />
      </NumberContainer>
      <Container
        key={page}
        id={items[index]}
        alt="slide"
        custom={direction}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          duration: 0.5
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={1}
        onDragEnd={(e, { offset, velocity }) => {
          const swipe = swipePower(offset.x, velocity.x);

          if (swipe < -swipeConfidenceThreshold) {
            paginate(1);
          } else if (swipe > swipeConfidenceThreshold) {
            paginate(-1);
          }
        }}>
        <AnimatePresence initial={false} custom={direction}>
          <Slide>
            <SVGContainer>
              {items[index].icon && (() => {
                const SVGIcon = SVGMapper[items[index].icon];
                return <SVGIcon />;
              })()}
            </SVGContainer>
            <TextContainer>
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`carousel-slide${
                    items[index].id === item.id ? ' carousel-slide--active' : ''
                  }`}
                  aria-hidden={items[index].id !== item.id}>
                  <h4>{item.id === 10 ? 10 : `0${item.id}`}</h4>
                  <h5
                    dangerouslySetInnerHTML={{
                      __html: windowWidth < 1200 ? item.title.replace('<br aria-hidden="true" />', ' ') : item.title
                    }}
                  />
                  <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.text) }} />
                </div>
              ))}
            </TextContainer>
          </Slide>
        </AnimatePresence>
      </Container>
    </>
  );
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const Title = styled.h3`
  justify-self: center;
  color: black;
  font-size: 2rem;
  line-height: 1.25em;
  margin-bottom: 1rem;
  letter-spacing: 0px;
  grid-column: 2/3;
  text-align: center;
  position: relative;

  svg {
    height: 100%;
    width: 300px;
    position: absolute;
    bottom: -0.75em;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (min-width: 48em) {
    font-size: 3rem;
  }
`;

const Description = styled.p`
  color: black;
  font-size: 1.25rem;
  text-align: center;
  max-width: 970px;
  margin: 0 auto;
`;

const Container = styled(motion.div)`
  align-items: center;
  display: flex;
  grid-column-gap: 1.25rem;
  grid-column: 1/6;
  grid-row-gap: 2.5rem;
  height: auto;
  justify-content: center;
  max-width: 100%;
  overflow-x: hidden !important;
  position: relative;

  @media (min-width: 1150px) {
    grid-column: 2/12;
  }

  .carousel-slide {
    display: none;
  }
  .carousel-slide--active {
    display: block;
  }
`;

const Slide = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 3rem;
  position: relative;
  width: 100%;
  justify-content: center;
  align-items: center;
  @media (min-width: 768px) {
    grid-template-columns: 150px 1fr;
    grid-row-gap: 1.5rem;
  }
  @media (min-width: 1150px) {
    grid-template-columns: 250px 1fr;
  }
`;

const SVGContainer = styled.div`
  text-align: center;
  width: auto;
  svg {
    width: 120px;
    max-width: 100%;
    max-height: 120px;
    height: auto;
    @media (min-width: 1150px) {
      width: 220px;
      max-height: 220px;
    }
  }
`;

const TextContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;

  h5 {
    color: black;
    font-size: 2.5rem;
    letter-spacing: 0px;
    line-height: 1em;
    margin: 0 auto 0.5em;
    text-align: center;
    text-transform: capitalize;

    @media (min-width: 768px) {
      font-size: 3rem;
      text-align: left;
      width: 100%;
    }
  }

  h4 {
    width: 100%;
    font-size: 2rem;
    line-height: 1.5em;
    font-weight: bold;
    margin-top: 0;
    margin-bottom: 0;
    color: var(--ax-gold);
    text-transform: uppercase;
    text-align: center;
    @media (min-width: 768px) {
      font-size: 2rem;
      text-align: left;
    }
  }
  p {
    color: black;
    text-align: center;
    font-size: 1rem;
    line-height: 1.5em;
    @media (min-width: 768px) {
      text-align: left;
    }
  }

  @media screen and (min-width: 48rem) {
    align-items: flex-start;
    min-height: 420px;
  }
`;

const Prev = styled(Arrow)`
  display: none;
  fill: ${({ disabled }) => (disabled ? 'grey' : '#CE9200')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  @media (min-width: 48rem) {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: block;
    left: 0;
    position: absolute;
    top: 45%;
    transform: rotate(180deg);
    width: 2.563em;
  }

  @media (min-width: 65.625rem) {
    left: 8rem;
  }

  @media screen and (min-width: 71.875rem) {
    left: -4.5rem;
    top: 37%;
  }
`;

const Next = styled(Arrow)`
  display: none;
  fill: ${({ disabled }) => (disabled ? 'grey' : '#CE9200')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  @media (min-width: 48rem) {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: block;
    position: absolute;
    right: 0;
    top: 45%;
    width: 2.563em;
  }

  @media (min-width: 65.625rem) {
    right: 8rem;
  }

  @media screen and (min-width: 71.875rem) {
    top: 37%;
    right: -4.5rem;
  }
`;

const NumberContainer = styled.div`
  display: grid;
  grid-column: 1/6;
  grid-gap: 0.5rem;
  grid-template-columns: repeat(5, minmax(40px, 100px));
  justify-content: center;
  margin-bottom: 2rem;
  margin: 0 auto 2.25em;
  max-width: 940px;
  position: relative;
  transition: 0.75s;
  width: 100%;

  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
  @media (min-width: 1150px) {
    grid-template-columns: repeat(10, minmax(40px, 100px));
    grid-column: 3/11;
    margin-top: 2rem;
  }
`;

const NumberBox = styled.button`
  background: ${({ selected }) => (selected ? 'var(--ax-gold) ' : 'var(--ex-beige)')};
  border: 1px solid var(--ax-gold);
  cursor: pointer;
  font-weight: bold;
  height: 0;
  outline: none;
  padding-top: 100%;
  position: relative;
  transition: 0.75s;
  width: 100%;

  :hover,
  :focus {
    border: 1px solid var(--ax-gold);
    outline: none;
  }
  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
    color: ${({ selected }) => (selected ? 'white' : '#C1BEAC')};
    @media (min-width: 768px) {
      font-size: 2rem;
    }
  }
`;

export default ServiceCarousel;
